import React, { useState } from "react";
import { ShieldCheck, Loader2 } from "lucide-react";
import CalculatorShell from "@/components/tools/CalculatorShell";
import { NumberInput } from "@/components/tools/Inputs";
import ResultsCard from "@/components/tools/ResultsCard";
import { useProducts } from "@/hooks/useProducts";
import { postTool } from "@/lib/api";
import { formatUSD, buildApplyQuery } from "@/lib/format";

export default function LifeInsuranceNeeds() {
  const { products } = useProducts();
  const [form, setForm] = useState({
    annual_income: 90000,
    years_to_replace: 10,
    total_debt: 250000,
    children_for_college: 2,
    final_expenses: 15000,
    existing_coverage: 100000,
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState("");
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    const e = {};
    if (form.annual_income < 0) e.annual_income = "Must be 0 or more.";
    if (form.years_to_replace < 0 || form.years_to_replace > 40)
      e.years_to_replace = "Use 0–40 years.";
    if (form.total_debt < 0) e.total_debt = "Must be 0 or more.";
    if (form.children_for_college < 0) e.children_for_college = "0 or more.";
    if (form.final_expenses < 0) e.final_expenses = "Must be 0 or more.";
    if (form.existing_coverage < 0) e.existing_coverage = "Must be 0 or more.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setLoading(true);
    setServerError("");
    setResult(null);
    try {
      await new Promise((r) => setTimeout(r, 500));
      const data = await postTool("life-insurance-needs", form);
      setResult(data);
    } catch {
      setServerError("Could not calculate. Please check your inputs.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <CalculatorShell
      title="Life Insurance Needs Calculator"
      blurb="The DIME method: how much coverage your family actually needs today."
      duration="Takes ~45 seconds"
      Icon={ShieldCheck}
    >
      <form
        onSubmit={onSubmit}
        data-testid="life-needs-form"
        className="bg-white rounded-2xl border border-lw-navy/10 p-6 sm:p-8 space-y-6"
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <NumberInput
            label="Annual income"
            value={form.annual_income}
            onChange={set("annual_income")}
            prefix="$"
            min={0}
            testId="life-income"
            error={errors.annual_income}
          />
          <NumberInput
            label="Years to replace income"
            value={form.years_to_replace}
            onChange={set("years_to_replace")}
            min={0}
            max={40}
            testId="life-years"
            error={errors.years_to_replace}
          />
          <NumberInput
            label="Total debt (mortgage + loans)"
            value={form.total_debt}
            onChange={set("total_debt")}
            prefix="$"
            min={0}
            testId="life-debt"
            error={errors.total_debt}
          />
          <NumberInput
            label="# of children to fund college for"
            value={form.children_for_college}
            onChange={set("children_for_college")}
            min={0}
            max={12}
            testId="life-children"
            helper="Estimated at $100,000 per child."
            error={errors.children_for_college}
          />
          <NumberInput
            label="Final expenses"
            value={form.final_expenses}
            onChange={set("final_expenses")}
            prefix="$"
            min={0}
            testId="life-final"
            helper="Typical estimate: $15,000."
            error={errors.final_expenses}
          />
          <NumberInput
            label="Existing life insurance"
            value={form.existing_coverage}
            onChange={set("existing_coverage")}
            prefix="$"
            min={0}
            testId="life-existing"
            error={errors.existing_coverage}
          />
        </div>

        {serverError && (
          <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
            {serverError}
          </p>
        )}

        <button
          type="submit"
          disabled={loading}
          data-testid="life-needs-submit"
          className="btn-gold w-full sm:w-auto disabled:opacity-60"
        >
          {loading ? (
            <>
              <Loader2 className="animate-spin" size={18} /> Calculating…
            </>
          ) : (
            <>Find my coverage gap →</>
          )}
        </button>
      </form>

      {result && (
        <ResultsCard
          source="life-insurance-needs"
          headline={`You may need ~${formatUSD(result.coverage_needed)} of life coverage`}
          subhead="Based on the DIME method: Debt + Income (×years) + Mortgage + Education."
          rows={[
            { label: "Income replacement", value: formatUSD(result.income_replacement), testId: "income-rep" },
            { label: "Debt", value: formatUSD(result.debt), testId: "debt" },
            { label: "College costs", value: formatUSD(result.college_costs), testId: "college" },
            { label: "Final expenses", value: formatUSD(result.final_expenses), testId: "final-exp" },
            { label: "Gross need", value: formatUSD(result.gross_need), testId: "gross" },
            { label: "Existing coverage", value: `– ${formatUSD(result.existing_coverage)}`, testId: "existing" },
          ]}
          assumptions={result.assumptions}
          matchedIds={result.matched_product_ids}
          products={products}
          applyQuery={buildApplyQuery({
            source: "life-needs",
            coverage: result.coverage_needed,
            income: form.annual_income,
            existing_coverage: form.existing_coverage,
            products: (result.matched_product_ids || []).join(","),
          })}
          toolSlug="life-insurance-needs"
          toolInputs={form}
          toolResults={result}
        />
      )}
    </CalculatorShell>
  );
}
